/**
 * POS Lock Mode Context.
 *
 * Fokus-Modus für die Kassenoberfläche auf einem Desktop-Terminal. Wenn
 * aktiv, blendet die Applikation Sidebar und Hauptnavigation aus und
 * zeigt nur noch die POS-Seite. Aktivierung + Verlassen erfordern eine
 * serverseitige Admin-Re-Authentifizierung (`POST /api/auth/verify-admin`),
 * nicht das Frontend.
 *
 * Persistenz: nur der REINE UI-STATE wird in ``localStorage`` gespeichert
 * (ein Flag, kein Passwort, kein Token). Beim Neuladen des Browsers
 * bleibt der Lock aktiv, das Verlassen fordert weiterhin ein Admin-
 * Passwort an. Damit wird niemals eine langlebige Credential im Browser
 * hinterlegt — die Session ist die reguläre HttpOnly-Cookie-Session.
 */
import React, { createContext, useContext, useEffect, useState } from "react";

const LS_KEY = "loop.pos_lock_mode";
const PosLockContext = createContext({
  isLocked: false,
  lock: () => {},
  unlock: () => {},
});

export function PosLockProvider({ children }) {
  const [isLocked, setIsLocked] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });

  const lock = () => {
    try { localStorage.setItem(LS_KEY, "1"); } catch {}
    setIsLocked(true);
  };
  const unlock = () => {
    try { localStorage.removeItem(LS_KEY); } catch {}
    setIsLocked(false);
  };

  // Cross-tab-Sync: wenn ein zweites Tab den Lock kippt, folgt dieses Tab.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LS_KEY) return;
      setIsLocked(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <PosLockContext.Provider value={{ isLocked, lock, unlock }}>
      {children}
    </PosLockContext.Provider>
  );
}

export const usePosLock = () => useContext(PosLockContext);
